// File: src/ProcessingHub.js
// Oct 2026: restyled for the hire form redesign (StepShell, real-state checklist,
//           no fixed countdown). Polling, routing and auth-expired logic unchanged.
// VERSION 2.3: 401/403 during polling stops immediately and shows a re-verify
//              prompt (was: silently retried into a dead-end 40s spinner —
//              the "spinning wheel of death", Mae/mae-hill.com incident Jul 2026)
// VERSION 2.2: Added blocked and referral handling for provisional licences and age restrictions
// VERSION 2.1: Added session token authentication for driver-status calls
// VERSION 2.0: Updated to route to new POA1/POA2 pages instead of old poa-validation
// Uses centralized get-next-step router

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StepShell, Card, PrimaryButton, Note, Spinner, PHASE, HELP_PHONE_DISPLAY, HELP_PHONE_TEL
} from './ui/HireFormUI';

// The "what's happening" checklist on the waiting screen. Module scope, never
// inside ProcessingHub (see CLAUDE.md: components declared inside components
// remount on every render).
// Has OP's router moved past the check this driver went to iDenfy for?
//
// This is how the hub knows the webhook has landed. It used to look for a
// fresh `idenfyCheckDate` on driver-status, but OP's /status has never
// returned that field (only the old Monday backend did), so in OP mode the
// test could not fire: every driver sat on "Checking your licence" for the
// full 40 seconds and then had to press "Carry on anyway" (jon, 20 Oct 2026 —
// webhook landed at 11:34:05, the hub polled on until 11:35:18).
//
// The router is the authority that sent them to iDenfy, so it's the right
// thing to ask. 'insurance-complete' is used because it re-checks the licence
// first ('processing-hub' skips that check). A licence session is done when
// the router stops saying 'idenfy'; a passport session when it stops saying
// 'passport-upload'. A rejected ID comes back as 'manual-review', which also
// counts — routeToNextStep then shows that screen. Any failure → "not yet".
async function routerHasMovedOn(email, isPassport) {
  try {
    const response = await fetch('/.netlify/functions/get-next-step', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, currentStep: 'insurance-complete' })
    });
    if (!response.ok) return false;
    const { nextStep } = await response.json();
    if (!nextStep) return false;
    const stillWaitingFor = isPassport ? ['passport-upload'] : ['idenfy', 'full-idenfy', 'selective-idenfy'];
    return !stillWaitingFor.includes(nextStep);
  } catch (error) {
    console.warn('Router check failed (treated as not yet):', error.message);
    return false;
  }
}

const Checklist = ({ items }) => (
  <div className="hf-checklist">
    {items.map(({ label, state }) => (
      <div key={label} className={`hf-check-item${state === 'done' ? ' is-done' : state === 'active' ? ' is-active' : ''}`}>
        {state === 'done' && <span className="hf-tick" aria-hidden="true">✓</span>}
        {state === 'active' && <span className="hf-pulse" aria-hidden="true" />}
        {state === 'todo' && <span className="hf-ring" aria-hidden="true" />}
        <span>{label}</span>
      </div>
    ))}
  </div>
);

const ProcessingHub = ({ driverEmail, jobId, sessionType }) => {
  console.log('🔍 ProcessingHub initialized:', { driverEmail, jobId, sessionType });
  
  const [status, setStatus] = useState('waiting');
  // The attempt count lives in attemptsRef; this state is kept only so a
  // retry resets it in step with the ref.
  const [, setAttempts] = useState(0);
  const [driverData, setDriverData] = useState(null);
  const [message, setMessage] = useState('Processing your verification...');
  const [blockReason, setBlockReason] = useState(null);      // v2.2: For blocked state
  const [referralReason, setReferralReason] = useState(null); // v2.2: For referral state
  const initialCheckDateRef = useRef(null);
  
  // Use refs to avoid recreating functions
  const attemptsRef = useRef(0);
  const intervalRef = useRef(null);
  
  const MAX_ATTEMPTS = 20;
  const POLL_INTERVAL = 2000;

  // v2.1: Helper to get session token for authenticated API calls
  const getSessionToken = () => {
    return sessionStorage.getItem('verificationSessionToken') || '';
  };

  // Route to next step using CENTRALIZED ROUTER
  const routeToNextStep = useCallback(async (data) => {
    console.log('🧭 CALLING CENTRALIZED ROUTER');
    
    if (!data) {
      console.error('❌ No data for routing - STOPPING');
      return;
    }
    
    try {
      // Call the centralized router
      const routerResponse = await fetch('/.netlify/functions/get-next-step', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: driverEmail,
          currentStep: 'idenfy-complete'  // ProcessingHub is after Idenfy
        })
      });
      
      if (!routerResponse.ok) {
        console.error('❌ Router call failed:', routerResponse.status);
        // Fallback to POA1 (new default)
        window.location.href = `/?step=poa1&email=${encodeURIComponent(driverEmail)}&job=${jobId}`;
        return;
      }
      
      const routerData = await routerResponse.json();
      console.log('✅ Router response:', routerData);
      
      const nextStep = routerData.nextStep;
      const reason = routerData.reason;
      
      console.log(`🚦 ROUTING TO: ${nextStep} (${reason})`);
      
      // v2.0: Updated step mapping for new POA flow
      // Router returns these steps, we map them to URL parameters
      const stepMapping = {
        // New POA flow (v2.0)
        'poa-instructions': 'poa-instructions',
        'poa1': 'poa1',
        'poa2': 'poa2',
        'dvla-check': 'dvla-check',
        
        // Other steps
        'passport-upload': 'passport-upload',
        'signature': 'signature',

        // Terminal — photo ID needs a human at Ooosh. MUST be mapped: the
        // lookup below falls back to 'poa1' for anything unrecognised, so an
        // unmapped step silently dumps the driver on a POA upload page instead
        // of the screen telling them we're reviewing their ID.
        'manual-review': 'manual-review',
        
        // Idenfy steps (shouldn't happen from ProcessingHub but just in case)
        'full-idenfy': 'document-upload',
        'selective-idenfy': 'document-upload',
        
        // Legacy mappings (for backwards compatibility if router returns old names)
        'poa-validation': 'poa1',      // Redirect old name to new page
        'dvla-processing': 'dvla-check' // Redirect old name to new page
      };
      
      // Use mapping or default to poa1 (new default, not old poa-validation)
      const urlStep = stepMapping[nextStep] || 'poa1';
      
      console.log(`📍 Navigating to: /?step=${urlStep}`);
      
      // Navigate to next step
      window.location.href = `/?step=${urlStep}&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;

    } catch (error) {
      console.error('❌ Error calling router:', error);
      // Fallback to POA1 (new default)
      window.location.href = `/?step=poa1&email=${encodeURIComponent(driverEmail)}&job=${jobId}&routed=1`;
    }
  }, [driverEmail, jobId]);

  // Check for webhook by monitoring idenfyCheckDate
  const checkWebhookProcessed = useCallback(async () => {
    try {
      // Use ref value for attempts
      const currentAttempt = attemptsRef.current + 1;
      attemptsRef.current = currentAttempt;
      setAttempts(currentAttempt);
      
      console.log(`🔄 Polling for webhook (attempt ${currentAttempt}/${MAX_ATTEMPTS})`);
      
      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = Date.now();
      // v2.1: Added session token header for authenticated API call
      const response = await fetch(`/.netlify/functions/driver-status?email=${encodeURIComponent(driverEmail)}&t=${cacheBuster}`, {
        headers: {
          'X-Session-Token': getSessionToken()
        }
      });
      
      if (!response.ok) {
        console.log(`📊 Driver status returned: ${response.status}`);

        // v2.3: 401/403 means our session token is missing or invalid —
        // polling again will NEVER succeed, so stop immediately and send the
        // driver back through the quick email re-verification instead of
        // burning 40 seconds into a dead-end spinner. Their progress is safe
        // (the Idenfy webhook writes server-side).
        if (response.status === 401 || response.status === 403) {
          console.warn('🔒 Session token missing/invalid during polling - prompting re-verification');
          setStatus('auth-expired');
          setMessage('Your session has expired');
          return;
        }

        if (currentAttempt < MAX_ATTEMPTS) {
          setMessage('Waiting for verification to process...');
          // Continue polling
          intervalRef.current = setTimeout(() => checkWebhookProcessed(), POLL_INTERVAL);
        } else {
          setStatus('timeout');
          setMessage('Verification is taking longer than expected');
        }
        return;
      }
      
      const data = await response.json();
      
      console.log('📊 Driver data retrieved:', {
        email: data?.email,
        idenfyCheckDate: data?.idenfyCheckDate,
        additionalDetails: data?.additionalDetails,
        overallStatus: data?.overallStatus,
        attempt: currentAttempt
      });
      
      // Check for webhook timestamp
      let webhookReceived = false;
      
      // Check if webhook timestamp exists AND is recent
      if (data?.idenfyCheckDate) {
        console.log('📅 Found idenfyCheckDate:', data.idenfyCheckDate);
        
        // Parse ISO timestamp
        try {
          const webhookTime = new Date(data.idenfyCheckDate);
          const now = new Date();
          const minutesAgo = (now - webhookTime) / (1000 * 60);
          
          console.log(`⏰ Webhook timestamp is ${minutesAgo.toFixed(1)} minutes old`);
          
          // If timestamp is recent (within 65 minutes), consider webhook received
          if (minutesAgo >= 0 && minutesAgo < 65) {
            console.log('✅ Recent webhook detected!');
            webhookReceived = true;
          } else if (minutesAgo < 0) {
            console.log('⚠️ Timestamp is in the future - possible timezone issue');
            // Still accept it if it's not too far in the future (65 minute tolerance)
            if (minutesAgo > -65) {
              webhookReceived = true;
            }
          } else {
            console.log('⏳ Timestamp too old - continuing to wait for fresh webhook');
          }
        } catch (e) {
          console.error('Failed to parse timestamp:', e);
          // Fallback: if we can't parse it but it exists, assume it's new
          if (!initialCheckDateRef.current || data.idenfyCheckDate !== initialCheckDateRef.current) {
            webhookReceived = true;
          }
        }
      }
               
      // The date test above can't fire in OP mode (see routerHasMovedOn).
      if (!webhookReceived && await routerHasMovedOn(driverEmail, sessionType === 'passport_only')) {
        console.log('✅ Router has moved on from the ID check - webhook processed');
        webhookReceived = true;
      }

      if (webhookReceived) {
        console.log('🎉 Webhook confirmed! Checking for blocks/referrals...');
        setDriverData(data);
        
        // =====================================================================
        // v2.2: CHECK FOR BLOCKED OR REFERRAL STATUS
        // =====================================================================
        const additionalDetails = data?.additionalDetails || '';
        
        // Check for BLOCKED status (hard stop - cannot continue)
        if (additionalDetails.startsWith('BLOCKED:')) {
          console.log('🚫 Driver is BLOCKED:', additionalDetails);
          const reason = additionalDetails.replace('BLOCKED:', '').trim();
          setBlockReason(reason);
          setStatus('blocked');
          return;
        }
        
        // Check for REFERRAL status (can continue but flagged)
        if (additionalDetails.startsWith('REFERRAL:')) {
          console.log('⚠️ Driver flagged for REFERRAL:', additionalDetails);
          const reason = additionalDetails.replace('REFERRAL:', '').trim();
          setReferralReason(reason);
          setStatus('referral');
          return;
        }
        // =====================================================================
        
        // Normal flow - no blocks or referrals
        setStatus('success');
        setMessage('Verification complete! Loading next step...');
        
        // Wait 7 seconds for all data to load, then route using centralized router
        setTimeout(() => {
          console.log('🚀 Routing to next step via centralized router...');
          routeToNextStep(data);
        }, 7000);
        
      } else {
        console.log('⏳ No webhook received yet - idenfyCheckDate:', data?.idenfyCheckDate);
        
        if (currentAttempt < MAX_ATTEMPTS) {
          // Update message based on time waited
          const secondsWaited = currentAttempt * 2;
          if (secondsWaited < 10) {
            setMessage('Waiting for verification to complete...');
          } else if (secondsWaited < 20) {
            setMessage('Processing your documents...');
          } else if (secondsWaited < 30) {
            setMessage('This is taking a bit longer than usual...');
          } else {
            setMessage('Almost there, please wait...');
          }
          
          // Continue polling
          intervalRef.current = setTimeout(() => checkWebhookProcessed(), POLL_INTERVAL);
          
        } else {
          console.log('⏱️ Timeout - webhook never arrived after 40 seconds');
          setStatus('timeout');
          setMessage('Verification is taking longer than expected');
        }
      }
      
    } catch (error) {
      console.error('❌ Error in webhook check:', error);
      
      const currentAttempt = attemptsRef.current;
      if (currentAttempt < MAX_ATTEMPTS) {
        intervalRef.current = setTimeout(() => checkWebhookProcessed(), POLL_INTERVAL);
      } else {
        setStatus('error');
        setMessage('An error occurred while processing');
      }
    }
  }, [driverEmail, sessionType, routeToNextStep, MAX_ATTEMPTS, POLL_INTERVAL]);

  const handleRetry = () => {
    console.log('🔄 Retry requested');
    setStatus('waiting');
    setAttempts(0);
    attemptsRef.current = 0;
    initialCheckDateRef.current = null; // Clear initial timestamp
    setBlockReason(null);
    setReferralReason(null);
    setMessage('Retrying verification check...');
    checkWebhookProcessed();
  };

  const handleManualContinue = async () => {
    console.log('⏭️ Manual continue requested - using centralized router');
    
    if (driverData) {
      await routeToNextStep(driverData);
    } else {
      // Call router without driver data (router will fetch it)
      try {
        const routerResponse = await fetch('/.netlify/functions/get-next-step', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: driverEmail,
            currentStep: 'processing-hub'
          })
        });
        
        if (routerResponse.ok) {
          const routerData = await routerResponse.json();
          
          // v2.0: Updated step mapping
          const stepMapping = {
            'poa-instructions': 'poa-instructions',
            'poa1': 'poa1',
            'poa2': 'poa2',
            'dvla-check': 'dvla-check',
            'passport-upload': 'passport-upload',
            'signature': 'signature',
            // Terminal — see the note on the primary mapping above.
            'manual-review': 'manual-review',
            // Legacy redirects
            'poa-validation': 'poa1',
            'dvla-processing': 'dvla-check'
          };
          
          const urlStep = stepMapping[routerData.nextStep] || 'poa1';
          window.location.href = `/?step=${urlStep}&email=${encodeURIComponent(driverEmail)}&job=${jobId}`;
        } else {
          // Fallback to POA1 (new default)
          window.location.href = `/?step=poa1&email=${encodeURIComponent(driverEmail)}&job=${jobId}`;
        }
      } catch (error) {
        console.error('Router call failed:', error);
        // Fallback to POA1 (new default)
        window.location.href = `/?step=poa1&email=${encodeURIComponent(driverEmail)}&job=${jobId}`;
      }
    }
  };

  // v2.2: Continue with referral (user acknowledges they'll need review)
  const handleContinueWithReferral = async () => {
    console.log('⏭️ Continue with referral requested');
    setStatus('success');
    setMessage('Continuing to next step...');
    
    // Short delay then route normally
    setTimeout(() => {
      if (driverData) {
        routeToNextStep(driverData);
      } else {
        handleManualContinue();
      }
    }, 1500);
  };

  // Start polling on mount
  useEffect(() => {
    console.log('🚀 ProcessingHub mounted - starting webhook poll');
    checkWebhookProcessed();
    
    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Oct 2026 redesign: every state sits in the step shell. The licence check
  // belongs to the "Licence" part; the passport check (non-UK licence) comes
  // after the address, so it belongs to "Sign".
  const isPassport = sessionType === 'passport_only';
  const docWord = isPassport ? 'passport' : 'licence';
  const phase = isPassport ? PHASE.SIGN : PHASE.LICENCE;

  // =========================================================================
  // RENDER: Waiting / success — the checklist follows REAL state, not a timer:
  // waiting = still polling for the iDenfy webhook; success = webhook landed,
  // now routing. (The old "N seconds remaining" countdown promised a fixed 40s.)
  // =========================================================================
  if (status === 'waiting' || status === 'success') {
    const checked = status === 'success';
    const items = [
      { label: 'Photos received', state: 'done' },
      { label: `Checking your ${docWord}`, state: checked ? 'done' : 'active' },
      { label: 'Working out your next step', state: checked ? 'active' : 'todo' }
    ];
    return (
      <StepShell jobId={jobId} phase={phase}>
        <Card className="hf-center">
          <Spinner />
          <div>
            <h2 className="hf-h2">Checking your {docWord}</h2>
            <p className="hf-intro" style={{ marginTop: 8 }}>
              This usually takes under a minute. Please keep this page open. It'll move on by itself.
            </p>
          </div>
          <div className="hf-inset hf-inset-lg" style={{ textAlign: 'left' }}>
            <Checklist items={items} />
          </div>
          <p className="hf-small hf-center">
            Closed it by mistake? Nothing's lost. Open the link in your email again and you'll pick up where you left off.
          </p>
        </Card>
      </StepShell>
    );
  }

  // =========================================================================
  // v2.2 RENDER: Blocked state (HARD STOP - cannot continue)
  // For: Provisional licence, Under 23
  // =========================================================================
  if (status === 'blocked') {
    return (
      <StepShell jobId={jobId} phase={phase}>
        <Card>
          <h2 className="hf-h2">We can't carry on with this form</h2>
          <p className="hf-intro">
            Unfortunately, we can't take this any further online.
          </p>
          <Note tone="amber">
            {blockReason || 'Your details don’t meet our insurer’s requirements.'}
          </Note>
          <p className="hf-body">
            If you think this is a mistake, or you'd like to talk it through, please give us a call.
          </p>
          <a href={HELP_PHONE_TEL} className="hf-btn">Call {HELP_PHONE_DISPLAY}</a>
        </Card>
      </StepShell>
    );
  }

  // =========================================================================
  // v2.2 RENDER: Referral state (CAN continue but flagged for review)
  // For: Over 75
  // =========================================================================
  if (status === 'referral') {
    return (
      <StepShell jobId={jobId} phase={phase}>
        <Card>
          <h2 className="hf-h2">We'll need to check with our insurer</h2>
          <p className="hf-intro">
            Your details fall outside our insurer's standard terms, so they'll need to look at them by hand.
          </p>
          <Note tone="amber">
            {referralReason || 'Your details need an extra check.'}
          </Note>
          <p className="hf-body">
            You can finish the form now and we'll be in touch. Or call us first to talk it through.
          </p>
          <PrimaryButton onClick={handleContinueWithReferral}>Carry on with the form</PrimaryButton>
          <a href={HELP_PHONE_TEL} className="hf-btn-secondary">Call {HELP_PHONE_DISPLAY}</a>
        </Card>
      </StepShell>
    );
  }

  // =========================================================================
  // v2.3 RENDER: Auth-expired state (session token missing/invalid)
  // Driver's documents ARE saved server-side — they just need to re-verify
  // their email and the router resumes at the right step.
  // =========================================================================
  if (status === 'auth-expired') {
    return (
      <StepShell jobId={jobId} phase={phase}>
        <Card>
          <h2 className="hf-h2">Please confirm your email again</h2>
          <p className="hf-intro">
            Your session timed out while we were checking your documents. Nothing's lost: confirm your email and you'll carry on where you left off.
          </p>
          <PrimaryButton
            onClick={() => {
              sessionStorage.clear();
              window.location.href = `/?job=${encodeURIComponent(jobId || '')}`;
            }}
          >
            Confirm my email
          </PrimaryButton>
        </Card>
      </StepShell>
    );
  }

  // =========================================================================
  // RENDER: Timeout state
  // =========================================================================
  if (status === 'timeout') {
    return (
      <StepShell jobId={jobId} phase={phase}>
        <Card>
          <h2 className="hf-h2">This is taking longer than usual</h2>
          <p className="hf-intro">Our ID partner is busy. Your photos are safe.</p>
          <PrimaryButton onClick={handleRetry}>Check again</PrimaryButton>
          <button type="button" className="hf-btn-secondary" onClick={handleManualContinue}>
            Carry on anyway
          </button>
        </Card>
      </StepShell>
    );
  }

  // =========================================================================
  // RENDER: Error state
  // =========================================================================
  if (status === 'error') {
    return (
      <StepShell jobId={jobId} phase={phase}>
        <Card>
          <h2 className="hf-h2">Something went wrong</h2>
          <p className="hf-intro">{message}</p>
          <PrimaryButton onClick={handleRetry}>Try again</PrimaryButton>
          <p className="hf-small hf-center">
            Still stuck? Call us on <a href={HELP_PHONE_TEL} className="hf-link">{HELP_PHONE_DISPLAY}</a>
          </p>
        </Card>
      </StepShell>
    );
  }

  return null;
};

export default ProcessingHub;
